<!-- routes/Register.svelte -->
<script>
  import { onMount } from "svelte";
  import { _ } from "svelte-i18n";
  import Header from "lib/components/Header.svelte";
  import Footer from "lib/components/Footer.svelte";
  import Container from "lib/components/Container.svelte";
  import Button from "lib/components/Button.svelte";
  import { trackEvent } from "utils/analytics.js";
  import {
    APP_URL,
    TERMS_AND_CONDITIONS,
    PRIVACY_POLICY,
    AML_POLICY,
    COOKIE_POLICY,
  } from "utils/menu.js";

  // Feature toggle for referral codes
  const referralCodesEnabled = false;

  // Form state
  let referralCode = "";
  let isSubmitting = false;
  let openFaq = null;

  $: faqItems = [
    {
      question: $_("register.faq.q1.question"),
      answer: $_("register.faq.q1.answer", {
        values: {
          feesLink: `<a href="/docs/fees" class="faq-link">${$_("register.faq.link.feesSchedule")}</a>`,
        },
      }),
    },
    {
      question: $_("register.faq.q2.question"),
      answer: $_("register.faq.q2.answer", {
        values: {
          email: `<a href="mailto:orders@liquidnoble.com" class="faq-link">orders@liquidnoble.com</a>`,
          officeLink: `<a href="/contact-us/" class="faq-link">${$_("register.faq.link.ourOffice")}</a>`,
          barringFeesLink: `<a href="/docs/fees/section/physical-redemption-fees" class="faq-link">${$_("register.faq.link.barringFees")}</a>`,
        },
      }),
    },
  ];

  function getAppUrl(referral) {
    return `${APP_URL}?signup=true${referral ? `&referral=${referral}` : ""}`;
  }

  function resetForm() {
    referralCode = "";
  }

  async function handleSubmit() {
    if (isSubmitting) return;

    isSubmitting = true;

    try {
      const url = getAppUrl(referralCodesEnabled ? referralCode : "");

      // Track registration attempt
      trackEvent("LoginRedirect", {
        referralCode: referralCodesEnabled ? (referralCode ?? "") : "",
        source: "register_page",
      });

      resetForm();
      window.location = url;
    } catch (error) {
      console.error("Registration redirect failed:", error);
      isSubmitting = false;
    }
  }

  onMount(() => {
    trackEvent("PageView", {
      page: "Register",
    });
  });
</script>

<svelte:head>
  <title>Register - Open Your Liquid Noble Account</title>
  <meta
    name="description"
    content="Open your Liquid Noble account now. Registration is quick and simple, giving you access to securely purchase, store, and transfer precious metals."
  />
</svelte:head>

<Header />

<Container variant="dark" size="large" class="register-container">
  <div class="register-content">
    <div class="register-text">
      <h1 class="register-title">{$_("register.title")}</h1>
      <p class="register-subtitle">
        {$_("register.subtitle")}
      </p>

      <div class="features-list">
        <div class="feature-item">
          <div class="feature-icon">🛡️</div>
          <span>{$_("register.feature1")}</span>
        </div>
        <div class="feature-item">
          <div class="feature-icon">⚡</div>
          <span>{$_("register.feature2")}</span>
        </div>
        <div class="feature-item">
          <div class="feature-icon">🏦</div>
          <div class="currency-content">
            <span>{$_("register.feature3.main")}</span>
            <div class="currency-list">
              <div class="currency-item">
                <img
                  src="/legal/images/Australia.svg"
                  alt="Australia"
                  class="flag-icon"
                />
                <span>AUD</span>
              </div>
              <div class="currency-item">
                <img src="/legal/images/EU.svg" alt="EU" class="flag-icon" />
                <span>EUR</span>
              </div>
              <div class="currency-item">
                <img src="/legal/images/USA.svg" alt="USA" class="flag-icon" />
                <span>USD</span>
              </div>
              <div class="currency-item">
                <img src="/legal/images/UK.svg" alt="UK" class="flag-icon" />
                <span>GBP</span>
              </div>
              <div class="currency-item">
                <img
                  src="/legal/images/New_Zealand.svg"
                  alt="New Zealand"
                  class="flag-icon"
                />
                <span>NZD</span>
              </div>
              <div class="currency-item">
                <img
                  src="/legal/images/Canada.svg"
                  alt="Canada"
                  class="flag-icon"
                />
                <span>CAD</span>
              </div>
              <div class="currency-item">
                <img
                  src="/legal/images/Singapore.svg"
                  alt="Singapore"
                  class="flag-icon"
                />
                <span>SGD</span>
              </div>
              <div class="currency-item">
                <img
                  src="/legal/images/Turkey.svg"
                  alt="Turkey"
                  class="flag-icon"
                />
                <span>TRY</span>
              </div>
            </div>
          </div>
        </div>
        <div class="feature-item">
          <div class="feature-icon">🎯</div>
          <span>{$_("register.feature4")}</span>
        </div>
      </div>

      {#if referralCodesEnabled}
        <!-- Legacy referral code UI -->
        <form on:submit|preventDefault={handleSubmit} class="register-form">
          <div class="form-section">
            <p class="referral-text">
              {$_("register.referralText")}
            </p>

            <div class="form-row">
              <div class="form-group">
                <input
                  type="text"
                  placeholder={$_("register.referralPlaceholder")}
                  maxlength="64"
                  bind:value={referralCode}
                  class="referral-input"
                />
              </div>

              <Button
                variant="primary"
                size="compact"
                type="submit"
                disabled={isSubmitting}
                class="register-button"
              >
                {isSubmitting
                  ? $_("register.buttonSubmitting")
                  : $_("register.button")}
              </Button>
            </div>
          </div>
        </form>
      {:else}
        <!-- New account opening UI -->
        <div class="account-opening-section">
          <div class="opening-card">
            <div class="opening-header">
              <div class="opening-icon">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
              <div class="opening-text">
                <h2 class="opening-title">{$_("register.opening.title")}</h2>
              </div>
            </div>

            <div class="benefits-grid">
              <div class="benefit-item">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>{$_("register.opening.benefit1")}</span>
              </div>
              <div class="benefit-item">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>{$_("register.opening.benefit2")}</span>
              </div>
              <div class="benefit-item">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>{$_("register.opening.benefit3")}</span>
              </div>
              <div class="benefit-item">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>{$_("register.opening.benefit4")}</span>
              </div>
            </div>

            <Button
              variant="primary"
              size="large"
              type="button"
              disabled={isSubmitting}
              class="open-account-button"
              on:click={handleSubmit}
            >
              {isSubmitting
                ? $_("register.buttonSubmitting")
                : $_("register.opening.button")}
            </Button>
          </div>
        </div>
      {/if}

      <div class="faq-section">
        {#each faqItems as item, i}
          <div class="faq-item" class:open={openFaq === i}>
            <button
              class="faq-question"
              on:click={() => {
                openFaq = openFaq === i ? null : i;
              }}
              aria-expanded={openFaq === i}
            >
              <span>{item.question}</span>
              <svg
                class="faq-chevron"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            {#if openFaq === i}
              <div class="faq-answer">
                {@html item.answer}
              </div>
            {/if}
          </div>
        {/each}
      </div>

      <div class="legal-notice">
        <p>
          {@html $_("register.legalNotice", {
            values: {
              terms: `<a href="${TERMS_AND_CONDITIONS}" target="_blank" rel="noopener noreferrer">${$_("register.legalTerms")}</a>`,
              privacy: `<a href="${PRIVACY_POLICY}" target="_blank" rel="noopener noreferrer">${$_("register.legalPrivacy")}</a>`,
              aml: `<a href="${AML_POLICY}" target="_blank" rel="noopener noreferrer">${$_("register.legalAml")}</a>`,
              cookie: `<a href="${COOKIE_POLICY}" target="_blank" rel="noopener noreferrer">${$_("register.legalCookie")}</a>`,
            },
          })}
        </p>
      </div>
    </div>

    <div class="register-visual">
      <div class="visual-container">
        <div class="gold-bars">
          <div class="gold-bar gold-bar-1">
            <div class="gold-shine"></div>
          </div>
          <div class="gold-bar gold-bar-2">
            <div class="gold-shine"></div>
          </div>
          <div class="gold-bar gold-bar-3">
            <div class="gold-shine"></div>
          </div>
        </div>

        <div class="stats-card">
          <div class="stat-item">
            <div class="stat-value">{$_("register.stat1.value")}</div>
            <div class="stat-label">{$_("register.stat1.label")}</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">{$_("register.stat2.value")}</div>
            <div class="stat-label">{$_("register.stat2.label")}</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">{$_("register.stat3.value")}</div>
            <div class="stat-label">{$_("register.stat3.label")}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</Container>

<Footer />

<style lang="scss">
  .register-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;
    min-height: 45vh;
    margin: 1rem 0;
  }

  .register-text {
    max-width: 600px;
  }

  .register-title {
    font-size: 2.8rem;
    font-weight: 800;
    color: var(--text-color1);
    margin-bottom: 1rem;
    line-height: 1.1;
  }

  .register-subtitle {
    font-size: 1.1rem;
    line-height: 1.6;
    color: var(--text-color2);
    margin-bottom: 1.8rem;
  }

  .features-list {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    margin-bottom: 2rem;
  }

  .feature-item {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    color: var(--text-color2);
    font-size: 0.95rem;
    text-align: left;
  }

  .feature-icon {
    width: 36px;
    height: 36px;
    background: rgba(210, 165, 82, 0.2);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    flex-shrink: 0;
  }

  /* Legacy referral code styles */
  .register-form {
    margin-bottom: 1.5rem;
  }

  .form-section {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 1rem;
    padding: 1.5rem;
    text-align: center;
  }

  .referral-text {
    color: var(--text-color2);
    font-size: 0.9rem;
    margin-bottom: 1.2rem;
    line-height: 1.5;
  }

  .form-row {
    display: flex;
    gap: 1rem;
    align-items: flex-end;
    justify-content: center;
  }

  .form-group {
    flex: 1;
    max-width: 250px;
  }

  .referral-input {
    width: 100%;
    padding: 1rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.1);
    color: var(--text-color2);
    font-size: 1rem;
    transition: all 0.3s ease;
    box-sizing: border-box;
    height: 48px;

    &:focus {
      outline: none;
      border-color: var(--text-color1);
      background: rgba(255, 255, 255, 0.15);
      box-shadow: 0 0 0 2px rgba(210, 165, 82, 0.2);
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
  }

  :global(.register-button) {
    white-space: nowrap;
    min-width: 180px;
  }

  /* New account opening styles */
  .account-opening-section {
    margin-bottom: 1.5rem;
  }

  .opening-card {
    background: linear-gradient(
      135deg,
      rgba(210, 165, 82, 0.15) 0%,
      rgba(210, 165, 82, 0.05) 100%
    );
    backdrop-filter: blur(10px);
    border: 1px solid rgba(210, 165, 82, 0.3);
    border-radius: 1.2rem;
    padding: 2rem;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }

  .opening-header {
    display: flex;
    gap: 1.2rem;
    align-items: flex-start;
    margin-bottom: 1.8rem;
  }

  .opening-icon {
    width: 56px;
    height: 56px;
    background: rgba(210, 165, 82, 0.2);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-color1);
    flex-shrink: 0;
  }

  .opening-text {
    flex: 1;
  }

  .opening-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-color1);
    margin-bottom: 0.5rem;
    line-height: 1.2;
  }

  .opening-description {
    font-size: 0.95rem;
    color: var(--text-color2);
    line-height: 1.5;
    margin: 0;
  }

  .benefits-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1.8rem;
  }

  .benefit-item {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    color: var(--text-color2);
    font-size: 0.9rem;

    svg {
      color: var(--text-color1);
      flex-shrink: 0;
    }
    > span {
      text-align: left;
    }
  }

  :global(.open-account-button) {
    width: 100%;
  }

  .legal-notice {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.6;
    margin-top: 0.5rem;

    a {
      color: var(--text-color1);
      text-decoration: none;
      transition: color 0.3s ease;
      font-size: 0.85rem;

      &:hover {
        color: #fff;
        text-decoration: underline;
      }
    }
  }

  .register-visual {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .visual-container {
    position: relative;
    width: 100%;
    max-width: 350px;
    height: 360px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .gold-bars {
    position: relative;
    width: 100%;
    height: 250px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1.2rem;
    transform: perspective(1000px) rotateY(-20deg) rotateX(10deg);
  }

  .gold-bar {
    width: 70px;
    height: 150px;
    background: linear-gradient(
      135deg,
      #f4d03f 0%,
      #f1c40f 20%,
      #e6ac00 40%,
      #d4af37 60%,
      #c9a328 80%,
      #b8941f 100%
    );
    border-radius: 6px;
    position: relative;
    box-shadow:
      0 15px 40px rgba(0, 0, 0, 0.4),
      0 5px 15px rgba(255, 215, 0, 0.3),
      inset 0 2px 10px rgba(255, 255, 255, 0.3),
      inset 0 -2px 10px rgba(0, 0, 0, 0.2);
    animation: float 6s ease-in-out infinite;
    overflow: hidden;

    &::before {
      content: "GOLD";
      position: absolute;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      width: 55px;
      height: 28px;
      background: linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.4) 0%,
        rgba(255, 255, 255, 0.1) 50%,
        rgba(255, 255, 255, 0.3) 100%
      );
      border-radius: 5px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.6rem;
      font-weight: bold;
      color: rgba(0, 0, 0, 0.6);
      text-shadow: 0 1px 2px rgba(255, 255, 255, 0.3);
      letter-spacing: 0.5px;
    }

    &::after {
      content: "999.9";
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      color: rgba(0, 0, 0, 0.6);
      font-size: 0.7rem;
      font-weight: bold;
      text-shadow: 0 1px 2px rgba(255, 255, 255, 0.3);
    }
  }

  .gold-bar-1 {
    animation-delay: 0s;
    z-index: 3;
  }

  .gold-bar-2 {
    animation-delay: 1s;
    z-index: 2;
  }

  .gold-bar-3 {
    animation-delay: 2s;
    z-index: 1;
  }

  .gold-shine {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.4) 50%,
      transparent 100%
    );
    animation: shine 3s ease-in-out infinite;
  }

  .stats-card {
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(15px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    padding: 1rem 1.2rem;
    display: flex;
    gap: 1.2rem;
    animation: slideUp 1s ease-out 0.5s both;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  }

  .stat-item {
    text-align: center;
    min-width: 50px;
  }

  .stat-value {
    font-size: 1.1rem;
    font-weight: bold;
    color: var(--text-color1);
    line-height: 1;
    margin-bottom: 0.2rem;
  }

  .stat-label {
    font-size: 0.75rem;
    color: var(--text-color2);
    font-weight: 500;
  }

  .currency-content {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .currency-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem 0.6rem;
    align-items: center;
  }

  .currency-item {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--text-color2);
  }

  .flag-icon {
    width: 16px;
    height: 12px;
    border-radius: 2px;
    object-fit: cover;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .currency-list {
      gap: 0.3rem 0.5rem;
    }

    .currency-item {
      font-size: 0.8rem;
    }

    .flag-icon {
      width: 14px;
      height: 10px;
    }

    .benefits-grid {
      grid-template-columns: 1fr;
    }

    .opening-header {
      flex-direction: column;
      align-items: center;
      text-align: center;
    }
  }

  @media (max-width: 480px) {
    .currency-list {
      gap: 0.25rem 0.4rem;
    }

    .currency-item {
      font-size: 0.75rem;
    }

    .flag-icon {
      width: 12px;
      height: 9px;
    }

    .opening-card {
      padding: 1.5rem;
    }

    .opening-title {
      font-size: 1.3rem;
    }
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px) rotateY(2deg);
    }
    50% {
      transform: translateY(8px) rotateY(-2deg);
    }
  }

  @keyframes shine {
    0% {
      left: -100%;
    }
    50% {
      left: 100%;
    }
    100% {
      left: 100%;
    }
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  /* Responsive Design */
  @media (max-width: 1024px) {
    .register-content {
      grid-template-columns: 1fr;
      gap: 2rem;
      text-align: center;
    }

    .register-text {
      max-width: none;
    }

    .visual-container {
      max-width: 300px;
      height: 320px;
    }

    .gold-bars {
      height: 200px;
    }

    .gold-bar {
      width: 60px;
      height: 120px;
    }
  }

  @media (max-width: 768px) {
    .register-title {
      font-size: 2.2rem;
    }

    .register-subtitle {
      font-size: 1rem;
    }

    .form-row {
      flex-direction: column;
      gap: 1rem;
      align-items: center;
    }

    .form-group {
      width: 100%;
      max-width: 280px;
    }

    .referral-input {
      width: 100%;
      text-align: center;
    }

    :global(.register-button) {
      width: 100%;
      min-width: auto;
    }

    .register-content {
      gap: 1.5rem;
    }

    .visual-container {
      max-width: 280px;
      height: 290px;
    }

    .gold-bars {
      height: 180px;
      gap: 0.8rem;
    }

    .gold-bar {
      width: 55px;
      height: 110px;
    }

    .stats-card {
      padding: 0.8rem;
      gap: 0.8rem;
    }
  }

  @media (max-width: 480px) {
    .register-title {
      font-size: 1.8rem;
    }

    .register-subtitle {
      font-size: 0.95rem;
    }

    .form-section {
      padding: 1.2rem;
    }

    .feature-item {
      font-size: 0.85rem;
    }

    .feature-icon {
      width: 32px;
      height: 32px;
      font-size: 1rem;
    }

    .legal-notice {
      font-size: 0.8rem;
    }

    .legal-notice a {
      font-size: 0.8rem;
    }
  }

  /* FAQ section styles */
  .faq-section {
    margin-top: 1.2rem;
    border-radius: 0.8rem;
    overflow: hidden;
    border: 1px solid rgba(210, 165, 82, 0.2);
    margin-bottom: 1.2rem;
  }

  .faq-item {
    border-bottom: 1px solid rgba(210, 165, 82, 0.15);

    &:last-child {
      border-bottom: none;
    }
  }

  .faq-question {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.85rem 1.1rem;
    background: rgba(210, 165, 82, 0.08);
    border: none;
    cursor: pointer;
    text-align: left;
    color: var(--text-color2);
    font-size: 0.9rem;
    font-weight: 600;
    transition:
      background 0.2s ease,
      color 0.2s ease;
    line-height: 1.4;

    &:hover {
      background: rgba(210, 165, 82, 0.14);
      color: var(--text-color1);
    }
  }

  .faq-chevron {
    flex-shrink: 0;
    transition: transform 0.25s ease;
    color: var(--text-color1);
    opacity: 0.7;
  }

  .faq-item.open .faq-chevron {
    transform: rotate(180deg);
  }

  .faq-item.open .faq-question {
    color: var(--text-color1);
    background: rgba(210, 165, 82, 0.12);
  }

  .faq-answer {
    padding: 0.9rem 1.1rem;
    background: rgba(0, 0, 0, 0.15);
    color: var(--text-color2);
    font-size: 0.88rem;
    line-height: 1.65;
    text-align: left;

    :global(.faq-link) {
      color: var(--text-color1);
      font-size: inherit;
      text-decoration: underline;
      text-underline-offset: 2px;
      transition: color 0.2s ease;

      &:hover {
        color: #fff;
      }
    }

    :global(strong) {
      color: var(--text-color2);
      font-weight: 600;
    }
  }

  @media (max-width: 480px) {
    .faq-question {
      font-size: 0.85rem;
      padding: 0.8rem 0.9rem;
    }

    .faq-answer {
      padding: 0.8rem 0.9rem;
      font-size: 0.84rem;
    }
  }
</style>
